type LogLevel = "debug" | "info" | "warn" | "error";

interface LoggerOptions {
  level: LogLevel;
  prefix?: string;
}

class Logger {
  private level: LogLevel;
  private prefix: string;

  constructor(options: LoggerOptions = { level: "info" }) {
    this.level = options.level;
    this.prefix = options.prefix || "";
  }

  private shouldLog(messageLevel: LogLevel): boolean {
    const levels: LogLevel[] = ["debug", "info", "warn", "error"];
    return levels.indexOf(messageLevel) >= levels.indexOf(this.level);
  }

  private formatMessage(level: LogLevel, ...args: any[]): string {
    const timestamp = new Date().toISOString();
    const prefix = this.prefix ? `[${this.prefix}]` : "";
    const message = args
      .map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg),
      )
      .join(" ");
    return `${timestamp} ${prefix} [${level.toUpperCase()}] ${message}`;
  }

  debug(...args: any[]): void {
    if (this.shouldLog("debug")) {
      console.debug(this.formatMessage("debug", ...args));
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog("info")) {
      console.info(this.formatMessage("info", ...args));
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog("warn")) {
      console.warn(this.formatMessage("warn", ...args));
    }
  }

  error(...args: any[]): void {
    if (this.shouldLog("error")) {
      console.error(this.formatMessage("error", ...args));
    }
  }
}

// Create a singleton instance with default options
const getLogLevel = (): LogLevel => {
  // Verificar variável de ambiente primeiro
  const envLevel = import.meta.env.VITE_LOG_LEVEL as LogLevel;
  if (envLevel && ["debug", "info", "warn", "error"].includes(envLevel)) {
    return envLevel;
  }
  
  // Fallback baseado no ambiente
  return process.env.NODE_ENV === "development" ? "warn" : "error";
};

export const logger = new Logger({
  level: getLogLevel(),
  prefix: "LicitarsAI",
});
